import React, { useEffect, useState } from 'react';
import { adaptiveAPI } from '../../services/api';

/**
 * LearnerSkillCardsPanel
 *
 * Panel showing per-learner FSRS card health for a given domain.
 * Designed to be embedded inside LearnerManagementPage's stats sidebar.
 *
 * Props:
 *   learnerId  — the learner's ID string
 *   domain     — the domain to filter by (optional; shows all if omitted)
 */
export default function LearnerSkillCardsPanel({ learnerId, domain }) {
  const [cards, setCards] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!learnerId) return;
    setLoading(true);
    adaptiveAPI.getLearnerCardHealth(learnerId, domain)
      .then(({ data }) => {
        const list = data?.cards || data?.results || data || [];
        setCards(Array.isArray(list) ? list : []);
      })
      .catch(() => setCards([]))
      .finally(() => setLoading(false));
  }, [learnerId, domain]);

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return iso;
    }
  };

  return (
    <div data-testid="learner-skill-cards-panel">
      {loading && (
        <div style={{ color: '#64748b', fontSize: 12, padding: '6px 0' }}>Loading review cards…</div>
      )}

      {!loading && (!cards || cards.length === 0) && (
        <div style={{ color: '#64748b', fontSize: 12, padding: '6px 0' }}>
          No review cards yet. The learner needs to complete skill practice first.
        </div>
      )}

      {!loading && cards && cards.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ color: '#64748b', textAlign: 'left' }}>
              <th style={{ paddingBottom: 4, fontWeight: 600 }}>Skill</th>
              <th style={{ paddingBottom: 4, fontWeight: 600, width: 90 }}>Due</th>
              <th style={{ paddingBottom: 4, fontWeight: 600, width: 65 }}>Stability</th>
              <th style={{ paddingBottom: 4, fontWeight: 600, width: 65 }}>Difficulty</th>
              <th style={{ paddingBottom: 4, fontWeight: 600, width: 50 }}>Reps</th>
              <th style={{ paddingBottom: 4, fontWeight: 600, width: 60 }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {cards.map((c, i) => {
              const skillId = c.skill_id || c.id || `card-${i}`;
              const isOverdue = Boolean(c.is_overdue);
              return (
                <tr
                  key={skillId}
                  style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}
                  data-testid={`learner-card-row-${skillId}`}
                >
                  <td style={{ padding: '5px 0', color: '#cbd5e1' }}>
                    {c.skill_label || c.label || skillId.split('.').pop()}
                  </td>
                  <td style={{ color: isOverdue ? '#f87171' : '#94a3b8' }}>
                    {formatDate(c.due_date)}
                  </td>
                  <td style={{ color: '#94a3b8' }}>
                    {c.stability != null ? c.stability.toFixed(1) : '—'}
                  </td>
                  <td style={{ color: '#94a3b8' }}>
                    {c.difficulty != null ? c.difficulty.toFixed(2) : '—'}
                  </td>
                  <td style={{ color: '#94a3b8' }}>
                    {c.reps ?? c.reviews ?? '—'}
                  </td>
                  <td>
                    {isOverdue ? (
                      <span style={{
                        display: 'inline-block',
                        padding: '1px 7px',
                        borderRadius: 999,
                        fontSize: 10,
                        fontWeight: 700,
                        color: '#f87171',
                        background: 'rgba(239,68,68,0.12)',
                        border: '1px solid rgba(239,68,68,0.3)',
                      }}>
                        Overdue
                      </span>
                    ) : (
                      <span style={{ color: '#475569', fontSize: 10 }}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
